import { useRouter } from 'next/router'
import { ChangeEvent, useEffect, useState } from 'react'
import { Switch } from '@headlessui/react'
import { classNames } from 'utils/dom-helpers'
import Translation from 'components/Translation'
import { checkCouponAvailability } from 'services/coupons'
import { toCurrency } from 'utils/string-localization-helper'
import { CouponApiResponse, CouponInput } from 'types/coupon'
import { DiscountType } from 'types/discount'
import { useAppointmentContext } from 'context/appointmentPurchaseContext/AppointmentPurchaseContainer'
import {
    default_border_color,
    default_primary_color,
} from '../../context/AuthContext'
import { useTenantContext } from '../../context/TenantContext'

export interface CouponFieldsetProps {
    id?: string | undefined
    coupon_code?: string
    discount?: number
    email?: string
    location?: Record<string, string>
    price?: number
    service_id?: string
    membership_id?: string
    tenant_id?: string
    label?: string
    onChange(coupon: CouponInput | null): void
    handleCouponChange?: (
        e: React.ChangeEvent<HTMLInputElement>
    ) => Promise<void>
}

// This component is different from the FREETRIAL field
// This is for additional discounts
export default function CouponFieldset(p: CouponFieldsetProps) {
    const { primaryColor } = useTenantContext()
    const router = useRouter()
    const url_code = router.query.coupon
    const { discount, email, price, service_id, membership_id } = p
    //@ts-ignore
    const finalMembershipId = membership_id
        ? membership_id
        : (p?.id as string | undefined)

    const {
        setIsCardWithCouponEnabled,
        setCouponCode,
        setCouponInfo,
        setIsValidCoupon,
    } = useAppointmentContext()
    const [has_coupon, showCouponInput] = useState<boolean>(
        ((url_code as string) || '').length > 0
    )
    const [is_loading, toggleInputLoading] = useState<boolean>()
    const [coupon_data, applyCoupon] = useState<CouponApiResponse>()
    const [code, setCode] = useState<string>(
        (router.query?.coupon as string) || ''
    )
    const [error_message, setErrorMessage] = useState<string>()

    function handleCouponCodeInputChange(e: ChangeEvent<HTMLInputElement>) {
        p.handleCouponChange && p?.handleCouponChange(e)
        setCode(e.target.value)
    }

    let timer = setTimeout(() => { }, 2000)
    useEffect(() => {
        applyCoupon(undefined)
        p.onChange(null)

        setIsCardWithCouponEnabled(has_coupon)

        if (has_coupon && code) {
            timer = setTimeout(() => {
                if (
                    email &&
                    has_coupon &&
                    code &&
                    (service_id || finalMembershipId)
                ) {
                    toggleInputLoading(true)
                    checkCouponAvailability({
                        coupon_code: code,
                        membership_id: finalMembershipId,
                        service_id,
                        email,
                    }).then(
                        (
                            check_results: CouponApiResponse & {
                                message?: string
                            }
                        ) => {
                            applyCoupon(check_results)
                            if (check_results.id) {
                                setIsValidCoupon(true)
                                setCouponInfo(check_results)
                                setCouponCode(code)
                                p.onChange({
                                    code: check_results.code,
                                    discount_value:
                                        check_results.discountValue,
                                    discount_type:
                                        check_results.discountType,
                                })
                            } else {
                                if (check_results?.message) {
                                    setErrorMessage(check_results?.message)
                                }
                                setIsValidCoupon(false)
                                setCouponInfo({})
                                setCouponCode('')
                            }
                            toggleInputLoading(false)
                        }
                    )
                }
            }, 2000)

            return () => {
                clearTimeout(timer)
            }
        } else {
            setIsValidCoupon(false)
            setCouponInfo(undefined)
            setCouponCode('')
        }
    }, [code, has_coupon, email, finalMembershipId, service_id])

    useEffect(() => {
        if (!has_coupon && !!router.query.coupon) {
            router.push({
                query: {
                    ...router.query,
                    coupon: null,
                },
            })
        }
    }, [has_coupon, router.query.coupon])

    return (
        <div className="mb-4">
            <fieldset>
                <label className="flex items-center gap-3 text-sm">
                    <Switch
                        checked={has_coupon}
                        onChange={showCouponInput}
                        className={classNames(
                            has_coupon
                                ? 'bg-primary'
                                : 'bg-primary bg-opacity-30',
                            'relative inline-flex flex-shrink-0 h-[24px] w-[48px] border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75'
                        )}
                    >
                        <span className="sr-only"><Translation content_key='got_coupon_disc' /></span>
                        <span
                            aria-hidden="true"
                            className={`${has_coupon
                                    ? 'translate-x-6'
                                    : 'translate-x-0'
                                }
            pointer-events-none inline-block h-[20px] w-[20px] rounded-full bg-white shadow-lg transform ring-0 transition ease-in-out duration-200`}
                        />
                    </Switch>
                    <Translation
                        content_key={p.label || 'booking.i_have_a_coupon'}
                        render_as="span"
                    />
                </label>
            </fieldset>
            <div
                className={classNames(
                    has_coupon ? '' : 'hidden',
                    'flex gap-3 items-center'
                )}
            >
                <fieldset className="w-72 mt-2">
                    <div className="w-full relative">
                        <input
                            className={`w-full rounded-lg border placeholder-gray-300 font-semibold ${is_loading
                                ? 'bg-gray-100 border-gray-300 text-gray-800'
                                : coupon_data != undefined &&
                                    !coupon_data.id &&
                                    code
                                    ? 'bg-red-100 bg-opacity-50 text-red-800 border-red-300'
                                    : ''
                                }`}
                            type="text"
                            id="coupon_code"
                            name="coupon_code"
                            defaultValue={code}
                            onChange={handleCouponCodeInputChange}
                        />
                        {coupon_data != undefined ? (
                            <i
                                className={classNames(
                                    coupon_data && coupon_data.id
                                        ? 'feather-check-circle text-green-500'
                                        : 'feather-x-circle text-red-500',
                                    'absolute right-3 top-[6px] text-xl'
                                )}
                            />
                        ) : (
                            <i className="feather-percent absolute right-3 top-[6px] text-xl text-gray-400" />
                        )}
                    </div>
                </fieldset>
                {error_message ? (
                    <div
                        className="text-xs w-72 leading-none"
                        style={{
                            color: `${primaryColor || default_primary_color
                                }80`,
                        }}
                    >
                        {error_message}
                    </div>
                ) : (
                    ''
                )}
            </div>
            <div>
                {discount && price ? (
                    <div className="text-sm my-2 max-w-2xl">
                        <Translation
                            content_key="coupon_successfully_applied"
                            content_values={{
                                d:
                                    coupon_data &&
                                        coupon_data.discountType ==
                                        DiscountType.PERCENT
                                        ? `${discount}%`
                                        : toCurrency(discount),
                            }}
                        />{' '}
                    </div>
                ) : (
                    ''
                )}
            </div>
        </div>
    )
}
